import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Mail, 
  Calendar, 
  Database, 
  Users, 
  Share2, 
  MessageSquare,
  Send,
  CheckCircle,
  Globe,
  Heart,
  DollarSign,
  Code,
  ShoppingCart,
  Settings,
  Menu,
  X,
  ChevronRight,
  Star,
  ArrowRight,
  Phone,
  MapPin,
  Clock,
  Shield,
  Award,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About Us', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Contact Us', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav 
        data-testid="navigation"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'glass nav-scrolled' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a 
              href="#home" 
              onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }}
              className="flex items-center gap-2"
              data-testid="logo"
            >
              <div className="w-10 h-10 rounded-xl bg-[#1A365D] flex items-center justify-center">
                <span className="font-montserrat font-bold text-white text-lg">VA</span>
              </div>
              <span className="font-montserrat font-bold text-xl text-[#1A365D]">vawork.net</span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="font-montserrat font-medium text-slate-700 hover:text-[#1A365D] transition-colors link-underline"
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </a>
              ))}
              <Button 
                onClick={() => scrollToSection('#contact')}
                className="btn-primary"
                data-testid="nav-cta"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-[#1A365D]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay md:hidden" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="fixed top-20 right-4 left-4 bg-white rounded-2xl shadow-xl p-6 z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="font-montserrat font-medium text-slate-700 hover:text-[#1A365D] py-2 transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <Button 
                onClick={() => scrollToSection('#contact')}
                className="btn-primary w-full mt-2"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="min-h-screen pt-20 hero-pattern relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-[#F6AD55]/10 text-[#1A365D] px-4 py-2 rounded-full mb-6">
              <Star className="w-4 h-4 fill-[#F6AD55] text-[#F6AD55]" />
              <span className="font-montserrat font-medium text-sm">Top 1% VA Talent</span>
            </div>
            
            <h1 className="font-montserrat font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#1A365D] tracking-tight leading-tight mb-6">
              Scale Your Business with{' '}
              <span className="text-[#F6AD55]">Elite Filipino</span>{' '}
              Virtual Assistants
            </h1>
            
            <p className="font-inter text-lg text-slate-600 mb-8 max-w-xl">
              Delegate your routine tasks to the top 1% of VA talent so you can focus on growth. 
              Save 20+ hours per week with our vetted professionals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button 
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary text-lg animate-pulse-glow"
                data-testid="hero-cta"
              >
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary text-lg"
                data-testid="hero-secondary-cta"
              >
                View Services
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="trust-badge">
                <Shield className="w-4 h-4 text-[#1A365D]" />
                <span>Vetted & Verified</span>
              </div>
              <div className="trust-badge">
                <Clock className="w-4 h-4 text-[#1A365D]" />
                <span>24/7 Support</span>
              </div>
              <div className="trust-badge">
                <Award className="w-4 h-4 text-[#1A365D]" />
                <span>100% Satisfaction</span>
              </div>
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="animate-fade-in-up animation-delay-300 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl image-overlay">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80" 
                alt="Professional team collaborating"
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 animate-float hidden lg:block">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-montserrat font-bold text-2xl text-[#1A365D]">500+</p>
                  <p className="text-slate-600 text-sm">Happy Clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Scope of Work Section
const ScopeOfWorkSection = () => {
  const services = [
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Admin Support",
      description: "Inbox zero, scheduling, calendar management, and meticulous data entry to keep your operations running smoothly.",
      color: "bg-blue-50",
      iconColor: "text-blue-600",
      featured: true
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Lead Management",
      description: "CRM updates, lead qualification, and outbound prospecting to fill your pipeline with qualified opportunities.",
      color: "bg-purple-50",
      iconColor: "text-purple-600"
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: "Social Media",
      description: "Content scheduling, community engagement, and brand presence management across all platforms.",
      color: "bg-pink-50",
      iconColor: "text-pink-600"
    },
    {
      icon: <Send className="w-8 h-8" />,
      title: "Email Marketing",
      description: "Campaign management, newsletter creation, and automated sequences that convert subscribers to customers.",
      color: "bg-orange-50",
      iconColor: "text-orange-600"
    }
  ];

  return (
    <section className="py-20 lg:py-28 section-alt" data-testid="scope-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="font-montserrat font-semibold text-[#F6AD55] text-sm uppercase tracking-wider">What We Do</span>
          <h2 className="font-montserrat font-bold text-3xl sm:text-4xl text-[#1A365D] mt-3 mb-4">
            Scope of Work
          </h2>
          <p className="font-inter text-slate-600 max-w-2xl mx-auto">
            Our elite virtual assistants handle your day-to-day tasks with precision and professionalism.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div 
              key={service.title}
              className={`bg-white rounded-2xl p-8 card-hover border border-slate-100 ${
                service.featured ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`scope-card-${index}`}
            >
              <div className={`service-icon ${service.color} ${service.iconColor} mb-6`}>
                {service.icon}
              </div>
              <h3 className="font-montserrat font-bold text-xl text-[#1A365D] mb-3">
                {service.title}
              </h3>
              <p className="font-inter text-slate-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: 500, suffix: '+', label: 'Happy Clients' },
    { value: 98, suffix: '%', label: 'Satisfaction Rate' },
    { value: 1000, suffix: '+', label: 'Tasks Completed Daily' },
    { value: 24, suffix: '/7', label: 'Support Available' },
  ];

  const Counter = ({ value, suffix }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (isVisible) {
        let start = 0;
        const duration = 2000;
        const increment = value / (duration / 16);
        
        const timer = setInterval(() => {
          start += increment;
          if (start >= value) {
            setCount(value);
            clearInterval(timer);
          } else {
            setCount(Math.floor(start));
          }
        }, 16);

        return () => clearInterval(timer);
      }
    }, [isVisible, value]);

    return (
      <span className="stat-number">{count}{suffix}</span>
    );
  };

  return (
    <section ref={sectionRef} className="py-20 bg-white" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {stats.map((stat, index) => (
            <div 
              key={stat.label}
              className="text-center"
              data-testid={`stat-${index}`}
            >
              <Counter value={stat.value} suffix={stat.suffix} />
              <p className="font-inter text-slate-600 mt-2">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const advantages = [
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Cultural Alignment",
      description: "High English proficiency and Western work ethics ensure seamless collaboration."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Dedication & Loyalty",
      description: "We take immense pride in our craftsmanship and build lasting partnerships."
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Cost-Efficiency",
      description: "Premium global talent at a competitive price point — maximize your ROI."
    }
  ];

  return (
    <section id="about" className="py-20 lg:py-28 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1497215842964-222b430dc094?w=800&q=80" 
                alt="Modern office in Manila"
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Accent decoration */}
            <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full rounded-3xl bg-[#F6AD55]/20"></div>
          </div>

          {/* Content */}
          <div>
            <span className="font-montserrat font-semibold text-[#F6AD55] text-sm uppercase tracking-wider">The Cultural Edge</span>
            <h2 className="font-montserrat font-bold text-3xl sm:text-4xl text-[#1A365D] mt-3 mb-6">
              About Us — The Filipino Advantage
            </h2>
            <p className="font-inter text-slate-600 text-lg mb-8 leading-relaxed">
              When you partner with VAWork.net, you're not just hiring assistants — you're gaining dedicated team members 
              who understand your business goals and are committed to your success.
            </p>

            <div className="space-y-6">
              {advantages.map((item, index) => (
                <div 
                  key={item.title}
                  className="flex gap-4"
                  data-testid={`advantage-${index}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#1A365D]/10 flex items-center justify-center text-[#1A365D]">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-montserrat font-semibold text-lg text-[#1A365D] mb-1">
                      {item.title}
                    </h3>
                    <p className="font-inter text-slate-600">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const generalServices = [
    { icon: <Calendar className="w-6 h-6" />, title: "Scheduling & Coordination" },
    { icon: <Database className="w-6 h-6" />, title: "Data Entry & Research" },
    { icon: <MessageSquare className="w-6 h-6" />, title: "Customer Support" },
    { icon: <Mail className="w-6 h-6" />, title: "Email Management" },
  ];

  return (
    <section id="services" className="py-20 lg:py-28 section-alt" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="font-montserrat font-semibold text-[#F6AD55] text-sm uppercase tracking-wider">Our Services</span>
          <h2 className="font-montserrat font-bold text-3xl sm:text-4xl text-[#1A365D] mt-3 mb-4">
            Comprehensive VA Solutions
          </h2>
          <p className="font-inter text-slate-600 max-w-2xl mx-auto">
            From general virtual assistance to specialized technical services through our partner network.
          </p>
        </div>

        {/* General VA Services */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {generalServices.map((service, index) => (
            <div 
              key={service.title}
              className="bg-white rounded-2xl p-6 text-center card-hover border border-slate-100"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-[#1A365D]/10 flex items-center justify-center text-[#1A365D] mx-auto mb-4">
                {service.icon}
              </div>
              <h3 className="font-montserrat font-semibold text-[#1A365D]">
                {service.title}
              </h3>
            </div>
          ))}
        </div>

        {/* ITDevwork Partner Section */}
        <div className="partner-card rounded-3xl p-8 lg:p-12 text-white" data-testid="partner-section">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
                <Code className="w-4 h-4 text-[#F6AD55]" />
                <span className="font-montserrat font-medium text-sm">Technical Partner</span>
              </div>
              <h3 className="font-montserrat font-bold text-2xl lg:text-3xl mb-4">
                Need Technical Muscle?
              </h3>
              <p className="font-inter text-white/80 text-lg mb-6 leading-relaxed">
                Our partners at <strong className="text-[#F6AD55]">ITDevwork.com</strong> specialize in WordPress, 
                Shopify, and Custom Software Development. Outsource your dev-stack to the experts.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Code className="w-5 h-5 text-[#F6AD55]" />
                  <span>WordPress</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <ShoppingCart className="w-5 h-5 text-[#F6AD55]" />
                  <span>Shopify</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Settings className="w-5 h-5 text-[#F6AD55]" />
                  <span>Custom Dev</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <a 
                href="https://itdevwork.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#F6AD55] text-[#1A365D] font-montserrat font-bold px-8 py-4 rounded-full hover:bg-[#ED8936] transition-all duration-300 transform hover:-translate-y-1 shadow-lg"
                data-testid="partner-cta"
              >
                Visit ITDevwork <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "VAWork has transformed how we operate. Our VA handles everything from email to lead gen, saving us 30+ hours weekly.",
      author: "Sarah Mitchell",
      role: "CEO, GrowthFirst Agency",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
    },
    {
      quote: "The quality of work is exceptional. Our Filipino VA feels like a true extension of our team.",
      author: "James Chen",
      role: "Founder, TechScale Solutions",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
    },
    {
      quote: "Best decision we made for our startup. Professional, reliable, and incredibly dedicated.",
      author: "Emily Rodriguez",
      role: "COO, Venture Labs",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="font-montserrat font-semibold text-[#F6AD55] text-sm uppercase tracking-wider">Testimonials</span>
          <h2 className="font-montserrat font-bold text-3xl sm:text-4xl text-[#1A365D] mt-3 mb-4">
            What Our Clients Say
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((item, index) => (
            <div 
              key={item.author}
              className="testimonial-card bg-slate-50 rounded-2xl p-8 border border-slate-100"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#F6AD55] text-[#F6AD55]" />
                ))}
              </div>
              <p className="font-inter text-slate-700 mb-6 leading-relaxed relative z-10">
                "{item.quote}"
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src={item.avatar} 
                  alt={item.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-montserrat font-semibold text-[#1A365D]">{item.author}</p>
                  <p className="font-inter text-sm text-slate-600">{item.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service_needed: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', service_needed: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-20 lg:py-28 section-alt" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Content */}
          <div>
            <span className="font-montserrat font-semibold text-[#F6AD55] text-sm uppercase tracking-wider">Get In Touch</span>
            <h2 className="font-montserrat font-bold text-3xl sm:text-4xl text-[#1A365D] mt-3 mb-6">
              Contact Us
            </h2>
            <p className="font-inter text-slate-600 text-lg mb-8 leading-relaxed">
              Ready to scale your business? Fill out the form and our team will reach out within 24 hours 
              to discuss how we can help.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1A365D]/10 flex items-center justify-center text-[#1A365D]">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-inter text-slate-600 text-sm">Email us at</p>
                  <p className="font-montserrat font-semibold text-[#1A365D]">hello@vawork.net</p>
                </div>
              </div>
              {/* <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1A365D]/10 flex items-center justify-center text-[#1A365D]">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-inter text-slate-600 text-sm">Call us at</p>
                  <p className="font-montserrat font-semibold text-[#1A365D]">+1 (555) 123-4567</p>
                </div>
              </div> */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1A365D]/10 flex items-center justify-center text-[#1A365D]">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-inter text-slate-600 text-sm">Based in</p>
                  <p className="font-montserrat font-semibold text-[#1A365D]">Manila, Philippines</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-container rounded-3xl p-8 lg:p-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-montserrat font-bold text-2xl text-[#1A365D] mb-3">Thank You!</h3>
                <p className="font-inter text-slate-600">We've received your message and will get back to you within 24 hours.</p>
                <Button 
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary mt-6"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="font-montserrat font-medium text-[#1A365D]">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="mt-2 rounded-xl border-slate-200 bg-slate-50 focus:ring-[#1A365D] focus:border-[#1A365D]"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="font-montserrat font-medium text-[#1A365D]">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="mt-2 rounded-xl border-slate-200 bg-slate-50 focus:ring-[#1A365D] focus:border-[#1A365D]"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <Label htmlFor="service_needed" className="font-montserrat font-medium text-[#1A365D]">Service Needed</Label>
                  <Select
                    value={formData.service_needed}
                    onValueChange={(value) => setFormData({ ...formData, service_needed: value })}
                  >
                    <SelectTrigger 
                      className="mt-2 rounded-xl border-slate-200 bg-slate-50"
                      data-testid="contact-service"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin-support">Admin Support</SelectItem>
                      <SelectItem value="lead-management">Lead Management</SelectItem>
                      <SelectItem value="social-media">Social Media Management</SelectItem>
                      <SelectItem value="email-marketing">Email Marketing</SelectItem>
                      <SelectItem value="technical-services">Technical Services (ITDevwork)</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message" className="font-montserrat font-medium text-[#1A365D]">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your needs..."
                    rows={4}
                    className="mt-2 rounded-xl border-slate-200 bg-slate-50 focus:ring-[#1A365D] focus:border-[#1A365D] resize-none"
                    data-testid="contact-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full text-lg"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer-gradient text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                <span className="font-montserrat font-bold text-[#1A365D] text-lg">VA</span>
              </div>
              <span className="font-montserrat font-bold text-xl">vawork.net</span>
            </div>
            <p className="font-inter text-white/70 max-w-md leading-relaxed mb-6">
              Scale your business with elite Filipino Virtual Assistants. Outsource your routine tasks 
              to the top 1% of VA talent and focus on what matters most — growing your business.
            </p>
            {/* SEO Keywords */}
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-white/50 bg-white/10 px-3 py-1 rounded-full">Filipino Virtual Assistants</span>
              <span className="text-xs text-white/50 bg-white/10 px-3 py-1 rounded-full">Remote Business Support</span>
              <span className="text-xs text-white/50 bg-white/10 px-3 py-1 rounded-full">Outsource WordPress Development</span>
              <span className="text-xs text-white/50 bg-white/10 px-3 py-1 rounded-full">Shopify Experts Philippines</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-montserrat font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                    className="font-inter text-white/70 hover:text-[#F6AD55] transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-montserrat font-semibold text-lg mb-6">Contact</h4>
            <ul className="space-y-3 font-inter text-white/70">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#F6AD55]" />
                hello@vawork.net
              </li>
              {/* <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#F6AD55]" />
                +1 (555) 123-4567
              </li> */}
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#F6AD55]" />
                Manila, Philippines
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-inter text-white/50 text-sm">
            © 2026 vawork.net. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="font-inter text-white/50 hover:text-white text-sm transition-colors">Privacy Policy</a>
            <a href="#" className="font-inter text-white/50 hover:text-white text-sm transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <ScopeOfWorkSection />
        <StatsSection />
        <AboutSection />
        <ServicesSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
